import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, AlertCircle, Eye, EyeOff, Lock, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useOAuthConfig from '../hooks/useOAuthConfig';
import { toast } from 'sonner';
import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const InviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, loginFromOAuth } = useAuth();
  const { getOAuthUrl, isCustomOAuth } = useOAuthConfig();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autoAccepting, setAutoAccepting] = useState(false);

  // Navigate-away warning while form has data
  useEffect(() => {
    const hasFormData = name.trim() || password || confirmPassword;
    if (!hasFormData) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [name, password, confirmPassword]);

  // Fetch invite details
  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/onboarding/invite/${token}`);
        if (!response.ok) {
          let message = 'This invitation is no longer valid or has expired.';
          try {
            const data = await response.json();
            message = data.detail || message;
          } catch { /* not json */ }
          throw new Error(message);
        }
        const data = await response.json();
        setInvite(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  // Auto-accept if already authenticated and email matches
  useEffect(() => {
    if (!invite || !isAuthenticated || !user || autoAccepting) return;
    if (user.email?.toLowerCase() !== invite.email?.toLowerCase()) return;

    const doAutoAccept = async () => {
      setAutoAccepting(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/onboarding/invite/${token}/accept`, {
          method: 'POST',
          credentials: 'include',
        });
        if (response.ok) {
          toast.success(`Welcome to ${invite.organization_name}!`);
          navigate('/products');
        } else {
          setAutoAccepting(false);
        }
      } catch {
        setAutoAccepting(false);
      }
    };
    doAutoAccept();
  }, [invite, isAuthenticated, user, token, navigate, autoAccepting]);

  const handleGoogleSignIn = () => {
    sessionStorage.setItem('pendingInvite', token);
    const callbackPath = isCustomOAuth() ? '/auth/google/callback' : '/auth/callback';
    const redirectUrl = window.location.origin + callbackPath;
    const oauthUrl = getOAuthUrl(redirectUrl);
    window.location.href = oauthUrl;
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/onboarding/invite/${token}/accept-with-credentials`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: name.trim(), password }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to join');
      }

      await loginFromOAuth(null, null);
      toast.success(`Welcome to ${invite.organization_name}!`);
      navigate('/products');
    } catch (err) {
      toast.error(err.message);
      setSubmitting(false);
    }
  };

  // Loading
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  // Auto-accepting
  if (autoAccepting) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Joining {invite?.organization_name}...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-md w-full relative z-10">
          <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-8 shadow-2xl text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Invalid Invitation</h1>
            <p className="text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Email mismatch
  if (isAuthenticated && user?.email?.toLowerCase() !== invite?.email?.toLowerCase()) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-md w-full relative z-10">
          <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-8 shadow-2xl text-center">
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Email Mismatch</h1>
            <p className="text-gray-300 mb-2">
              This invite was sent to <strong className="text-white">{invite?.email}</strong>.
            </p>
            <p className="text-gray-400">
              You are signed in as <strong>{user?.email}</strong>. Please sign out and use the correct account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main invite page
  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background gradient effects — matches login */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-md w-full relative z-10">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-xl font-semibold text-white tracking-tight">
              StackRanked.ai
            </span>
          </div>
          <h1 data-testid="invite-heading" className="text-3xl font-bold text-white mb-2">You're invited</h1>
          <p className="text-gray-400">
            Join <strong className="text-white">{invite?.organization_name}</strong> on StackRanked.ai
          </p>
          {invite?.invited_by && (
            <p className="text-sm text-gray-500 mt-1">Invited by {invite.invited_by}</p>
          )}
        </div>

        {/* Invite Card */}
        <div className="bg-[#0a0a0a] rounded-xl border border-white/10 p-8 shadow-2xl">

          {/* Google Sign-In */}
          <Button
            data-testid="invite-google-btn"
            onClick={handleGoogleSignIn}
            className="w-full bg-white hover:bg-gray-100 text-gray-900 font-medium py-6 flex items-center justify-center gap-3 transition-all"
          >
            <GoogleLogo />
            Continue with Google
          </Button>

          {/* Divider — matches login */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0a0a0a] text-gray-500">or</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleCredentialsSubmit} className="space-y-4">
            {/* Email — display only */}
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input
                data-testid="invite-email-field"
                type="email"
                value={invite?.email || ''}
                disabled
                className="mt-1 bg-[#111] border-white/10 text-gray-500 cursor-not-allowed"
              />
            </div>

            {/* Name */}
            <div>
              <Label className="text-gray-300">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="invite-name-field"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <Label className="text-gray-300">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="invite-password-field"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                  className="pl-10 pr-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <Label className="text-gray-300">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="invite-confirm-password-field"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  minLength={8}
                  className="pl-10 bg-[#111] border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
                />
              </div>
            </div>

            <Button
              data-testid="invite-join-btn"
              type="submit"
              disabled={submitting}
              className="w-full py-6 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Joining...
                </>
              ) : (
                `Join ${invite?.organization_name || 'Organization'}`
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteAcceptPage;
